import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import CodeMirror, { type ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { saveShortcutLabel } from '@/hooks/useSaveShortcut';
import { apiClient, getErrorMessage, normalizeBlobError } from '@/lib/apiClient';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { documentTemplateService } from '@/services/core/DocumentTemplateService';
import { documentPageSetupService } from '@/services/core/DocumentPageSetupService';
import { DocumentPageSetup } from '@/types/core/DocumentPageSetup';
import { DocumentTemplateCreateSchema } from '@/forms/core/DocumentTemplateSchema';
import {
  parseTemplate,
  serializeTemplate,
  type ParsedTemplate,
} from './documentTemplates/scribanHtmlTransform';
import { checkVisualFidelity } from './documentTemplates/tiptapScribanExtensions';
import {
  DocumentTemplateVisualEditor,
  type VisualEditorHandle,
} from './documentTemplates/DocumentTemplateVisualEditor';

const PREVIEW_DEBOUNCE_MS = 800;

interface PaletteEntry {
  /** Scriban expression the chip inserts, e.g. "{{ customer.name }}" or a for-loop block. */
  snippet: string;
  /** Short label shown on the chip, e.g. "customer.name" or "lines (loop)". */
  label: string;
  /** Bare expression (no braces) for visual-mode chip insertion; unset for loop entries. */
  expr?: string;
  /** Loop entries insert a `{{ for }}` block — code view only. */
  isLoop?: boolean;
}

/**
 * Flattens sample data into palette entries. Scalars become `{{ path }}` chips; arrays become a
 * `{{ for item in path }}...{{ end }}` block chip plus one chip per item field.
 */
function buildPalette(sample: Record<string, unknown>, prefix = ''): PaletteEntry[] {
  const entries: PaletteEntry[] = [];
  for (const [key, value] of Object.entries(sample)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (Array.isArray(value)) {
      const loopVar = key.endsWith('s') ? key.slice(0, -1) : 'item';
      entries.push({
        snippet: `{{ for ${loopVar} in ${path} }}\n\n{{ end }}`,
        label: `${path} (loop)`,
        isLoop: true,
      });
      const first = value[0];
      if (first && typeof first === 'object') {
        for (const itemKey of Object.keys(first as Record<string, unknown>)) {
          const expr = `${loopVar}.${itemKey}`;
          entries.push({ snippet: `{{ ${expr} }}`, label: expr, expr });
        }
      }
    } else if (value && typeof value === 'object') {
      entries.push(...buildPalette(value as Record<string, unknown>, path));
    } else {
      entries.push({ snippet: `{{ ${path} }}`, label: path, expr: path });
    }
  }
  return entries;
}

const documentTypeOptions: string[] =
  ((DocumentTemplateCreateSchema as { properties?: { documentType?: { enum?: string[] } } })
    .properties?.documentType?.enum) ?? ['INVOICE'];

/**
 * Template editor (Phase 4 of the PDF design spec): raw HTML/Scriban source on the left,
 * placeholder palette derived from the document type's sample data, and a debounced
 * live PDF preview on the right (POST /preview renders unsaved content against sample data;
 * placeholder typos come back as validation errors thanks to strict rendering).
 */
export const DocumentTemplateEditorView = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState(documentTypeOptions[0]);
  const [htmlContent, setHtmlContent] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [description, setDescription] = useState('');
  // '' means "no page setup selected" — the renderer's built-in default applies.
  const [pageSetupId, setPageSetupId] = useState('');
  const [pageSetups, setPageSetups] = useState<DocumentPageSetup[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [palette, setPalette] = useState<PaletteEntry[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);

  const [viewMode, setViewMode] = useState<'code' | 'visual'>('code');
  // Reasons the current source cannot enter the visual editor (empty = toggle enabled).
  const [visualBlockReasons, setVisualBlockReasons] = useState<string[]>([]);
  const parsedRef = useRef<ParsedTemplate | null>(null);
  // Exact source at visual-mode entry, restored verbatim if the user leaves without editing —
  // just peeking at the visual view must never churn the code formatting.
  const entrySnapshotRef = useRef('');
  const visualDirtyRef = useRef(false);
  const visualEditorRef = useRef<VisualEditorHandle>(null);

  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const previewUrlRef = useRef<string | null>(null);
  const theme = useTheme();

  // Load the template (or the embedded default for a new one) + the palette's sample data.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isNew) {
          const { data } = await apiClient.get<string>(
            '/api/core/documenttemplate/default-content',
            { params: { documentType: documentTypeOptions[0] } },
          );
          if (!cancelled) setHtmlContent(data);
        } else if (id) {
          const template = await documentTemplateService.getById(id);
          if (template && !cancelled) {
            setName(template.name);
            setDocumentType(template.documentType);
            setHtmlContent(String(template.htmlContent ?? ''));
            setIsActive(template.isActive);
            setIsDefault(template.isDefault);
            setDescription(String(template.description ?? ''));
            setPageSetupId(template.pageSetupId ?? '');
          }
        }
      } catch (error) {
        setApiValidationErrors([
          { field: 'form', message: getErrorMessage(error), instancePath: '#', members: ['form'] },
        ]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // Selectable page setups for the "Page setup" picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const setups = await documentPageSetupService.getAll();
        if (!cancelled) setPageSetups(setups);
      } catch {
        if (!cancelled) setPageSetups([]); // Picker is a convenience — never block editing on it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Palette follows the selected document type.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get<string>('/api/core/documenttemplate/sample-data', {
          params: { documentType },
        });
        const sample = typeof data === 'string' ? JSON.parse(data) : data;
        if (!cancelled) setPalette(buildPalette(sample));
      } catch {
        if (!cancelled) setPalette([]); // Palette is a convenience — never block editing on it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentType]);

  // Debounced live preview of the current (unsaved) buffer.
  useEffect(() => {
    if (loading || !htmlContent.trim()) return;
    const timer = setTimeout(async () => {
      setPreviewing(true);
      try {
        const response = await apiClient.post(
          '/api/core/documenttemplate/preview',
          { documentType, htmlContent, pageSetupId: pageSetupId || null },
          { responseType: 'blob' },
        );
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
        setApiValidationErrors((prev) => prev.filter((e) => e.field !== 'htmlContent'));
      } catch (rawError) {
        const error = await normalizeBlobError(rawError);
        const errors = getValidationErrors(error);
        setApiValidationErrors(
          errors.length > 0
            ? errors
            : [{ field: 'htmlContent', message: getErrorMessage(error), instancePath: '#/properties/htmlContent', members: ['htmlContent'] }],
        );
      } finally {
        setPreviewing(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [htmlContent, documentType, pageSetupId, loading]);

  // Revoke the last object URL on unmount.
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  const insertSnippet = useCallback((snippet: string) => {
    const view = editorRef.current?.view;
    if (!view) return;
    const { from, to } = view.state.selection.main;
    // The dispatched change flows back through CodeMirror's onChange, which updates
    // htmlContent and hasUnsavedChanges.
    view.dispatch({
      changes: { from, to, insert: snippet },
      selection: { anchor: from + snippet.length },
    });
    view.focus();
  }, []);

  // Pretty-print the source with Prettier's browser build (loaded on demand — it stays out of
  // the main bundle). Scriban `{{ ... }}` expressions are plain text to the HTML parser, so
  // they survive formatting; genuinely malformed HTML surfaces as a validation error instead.
  const handleFormat = async () => {
    try {
      const [prettier, htmlPlugin, cssPlugin] = await Promise.all([
        import('prettier/standalone'),
        import('prettier/plugins/html'),
        import('prettier/plugins/postcss'),
      ]);
      const formatted = await prettier.format(htmlContent, {
        parser: 'html',
        plugins: [htmlPlugin, cssPlugin],
      });
      if (formatted !== htmlContent) {
        setHtmlContent(formatted);
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      setApiValidationErrors([
        {
          field: 'htmlContent',
          message: getErrorMessage(error),
          instancePath: '#/properties/htmlContent',
          members: ['htmlContent'],
        },
      ]);
    }
  };

  // Keep the visual toggle's enabled state in sync with the source while in code mode.
  useEffect(() => {
    if (loading || viewMode !== 'code') return;
    const result = parseTemplate(htmlContent);
    setVisualBlockReasons(result.ok ? [] : result.reasons);
  }, [htmlContent, viewMode, loading]);

  // Fires synchronously on the first visual edit so mode switches and save flush the editor
  // content even before the debounced serialization below has run.
  const handleVisualDirty = useCallback(() => {
    visualDirtyRef.current = true;
    setHasUnsavedChanges(true);
  }, []);

  // Debounced edits from the visual editor: serialize back to source so the existing
  // preview pipeline (and save) keep working off htmlContent unchanged.
  const handleVisualChange = useCallback((bodyHtml: string) => {
    const parsed = parsedRef.current;
    if (!parsed) return;
    setHtmlContent(serializeTemplate(parsed.prefix, parsed.suffix, bodyHtml));
  }, []);

  const handleViewModeChange = (next: 'code' | 'visual' | null) => {
    if (!next || next === viewMode) return;
    if (next === 'visual') {
      const result = parseTemplate(htmlContent);
      const fidelity = result.ok ? checkVisualFidelity(result.template.editableBodyHtml) : result;
      if (!result.ok || !fidelity.ok) {
        const reasons = result.ok ? (fidelity as { reasons: string[] }).reasons : result.reasons;
        setVisualBlockReasons(reasons);
        setApiValidationErrors(
          reasons.map((message) => ({
            field: 'htmlContent',
            message,
            instancePath: '#/properties/htmlContent',
            members: ['htmlContent'],
          })),
        );
        return;
      }
      parsedRef.current = result.template;
      entrySnapshotRef.current = htmlContent;
      visualDirtyRef.current = false;
      setViewMode('visual');
    } else {
      setHtmlContent(currentSourceHtml());
      setViewMode('code');
    }
  };

  /** Source of truth for save/mode-switch: flushes unserialized visual edits synchronously. */
  const currentSourceHtml = () => {
    if (viewMode === 'visual' && parsedRef.current && visualEditorRef.current) {
      if (!visualDirtyRef.current) return entrySnapshotRef.current;
      return serializeTemplate(
        parsedRef.current.prefix,
        parsedRef.current.suffix,
        visualEditorRef.current.getBodyHtml(),
      );
    }
    return htmlContent;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const sourceHtml = currentSourceHtml();
      if (sourceHtml !== htmlContent) setHtmlContent(sourceHtml);
      const payload = {
        name,
        documentType,
        htmlContent: sourceHtml,
        isActive,
        isDefault,
        description,
        pageSetupId: pageSetupId || undefined,
      };
      if (isNew) {
        const created = await documentTemplateService.create(payload);
        navigate(`/core/documenttemplates/${created.id}`, { replace: true });
      } else if (id) {
        await documentTemplateService.update(id, payload);
      }
      setHasUnsavedChanges(false);
      setApiValidationErrors([]);
    } catch (error) {
      const errors = getValidationErrors(error);
      setApiValidationErrors(
        errors.length > 0
          ? errors
          : [{ field: 'form', message: getErrorMessage(error), instancePath: '#', members: ['form'] }],
      );
      setValidationPanelOpen(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const saveDisabled = saving || !name.trim() || !htmlContent.trim();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ViewToolbar
        showBackButton
        onBack={() => navigate('/core/documenttemplates')}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSave}
        saveDisabled={saveDisabled}
      >
        <Typography variant="h6" sx={{ mr: 2 }}>
          {isNew ? t('documentTemplate.newTitle') : name || t('documentTemplate.title')}
        </Typography>
        <Tooltip title={saveShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              onClick={handleSave}
              disabled={saveDisabled}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                ...(saveDisabled
                  ? { borderColor: 'grey.300', color: 'grey.400' }
                  : { borderColor: 'primary.main', color: 'primary.main' }),
              }}
            >
              {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
            </Button>
          </span>
        </Tooltip>
      </ViewToolbar>

      <ValidationErrorBanner
        errors={apiValidationErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => setApiValidationErrors([])}
      />
      <ValidationErrorPanel
        errors={apiValidationErrors}
        open={validationPanelOpen}
        onClose={() => setValidationPanelOpen(false)}
      />

      <Box sx={{ display: 'flex', gap: 2, p: 2, flex: 1, minHeight: 0 }}>
        {/* Left: metadata + source editor. The editor grows with its content, so the pane
            scrolls instead of the code overflowing the card. */}
        <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'auto' }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
              label={t('documentTemplate.name', 'Name')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setHasUnsavedChanges(true);
              }}
              size="small"
              required
              sx={{ flex: 2 }}
            />
            <TextField
              label={t('documentTemplate.documentType', 'Document type')}
              value={documentType}
              onChange={(e) => {
                setDocumentType(e.target.value);
                setHasUnsavedChanges(true);
              }}
              size="small"
              select
              disabled={!isNew}
              sx={{ flex: 1 }}
            >
              {documentTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('documentTemplate.pageSetup', 'Page setup')}
              value={pageSetupId}
              onChange={(e) => {
                setPageSetupId(e.target.value);
                setHasUnsavedChanges(true);
              }}
              size="small"
              select
              sx={{ flex: 1 }}
            >
              <MenuItem value="">{t('documentTemplate.pageSetupDefault', 'Default (A4, 15 mm)')}</MenuItem>
              {pageSetups.map((setup) => (
                <MenuItem key={setup.id} value={setup.id}>
                  {setup.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => {
                    setIsActive(e.target.checked);
                    setHasUnsavedChanges(true);
                  }}
                />
              }
              label={t('documentTemplate.isActive', 'Active')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isDefault}
                  onChange={(e) => {
                    setIsDefault(e.target.checked);
                    setHasUnsavedChanges(true);
                  }}
                />
              }
              label={t('documentTemplate.isDefault', 'Default')}
            />
          </Stack>

          <TextField
            label={t('documentTemplate.description', 'Description')}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setHasUnsavedChanges(true);
            }}
            size="small"
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('documentTemplate.palette', 'Placeholders — click to insert at cursor')}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {viewMode === 'code' && (
                <Tooltip title={t('documentTemplate.formatCode', 'Format code')}>
                  <IconButton size="small" onClick={handleFormat}>
                    <FormatAlignLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={
                  viewMode === 'code' && visualBlockReasons.length > 0 ? visualBlockReasons.join(' • ') : ''
                }
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={viewMode}
                  onChange={(_, next) => handleViewModeChange(next)}
                >
                  <ToggleButton value="code" sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}>
                    {t('documentTemplate.codeView', 'Code')}
                  </ToggleButton>
                  <ToggleButton
                    value="visual"
                    disabled={viewMode === 'code' && visualBlockReasons.length > 0}
                    sx={{ textTransform: 'none', px: 1.5, py: 0.25 }}
                  >
                    {t('documentTemplate.visualView', 'Visual')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            </Stack>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {palette.map((entry) => {
              const loopDisabled = viewMode === 'visual' && Boolean(entry.isLoop);
              return (
                <Tooltip
                  key={entry.label}
                  title={
                    loopDisabled
                      ? t('documentTemplate.loopCodeOnly', 'Insert loops in the code view')
                      : entry.snippet
                  }
                >
                  <span>
                    <Chip
                      label={entry.label}
                      size="small"
                      disabled={loopDisabled}
                      onClick={() => {
                        if (viewMode === 'visual') {
                          if (entry.expr) visualEditorRef.current?.insertExpression(entry.expr);
                        } else {
                          insertSnippet(entry.snippet);
                        }
                      }}
                    />
                  </span>
                </Tooltip>
              );
            })}
          </Box>

          {viewMode === 'code' ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                // overflow:hidden zeroes the flex min-size, letting this box shrink-clip
                // instead of pushing the pane into scroll — flexShrink 0 restores that.
                flexShrink: 0,
                overflow: 'hidden',
                '& .cm-editor': { fontSize: 13 },
              }}
            >
              <CodeMirror
                ref={editorRef}
                value={htmlContent}
                extensions={[html()]}
                theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
                minHeight="400px"
                onChange={(value) => {
                  setHtmlContent(value);
                  setHasUnsavedChanges(true);
                }}
              />
            </Box>
          ) : (
            parsedRef.current && (
              <DocumentTemplateVisualEditor
                ref={visualEditorRef}
                initialBodyHtml={parsedRef.current.editableBodyHtml}
                styleText={parsedRef.current.styleText}
                paletteOptions={palette.flatMap((entry) => (entry.expr ? [entry.expr] : []))}
                onDirty={handleVisualDirty}
                onDirtyChange={handleVisualChange}
              />
            )
          )}
        </Paper>

        {/* Right: live PDF preview */}
        <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">{t('documentTemplate.preview', 'Preview (sample data)')}</Typography>
            {previewing && <CircularProgress size={14} />}
          </Box>
          {previewUrl ? (
            <Box
              component="iframe"
              src={previewUrl}
              title={t('documentTemplate.previewFrame')}
              sx={{ flex: 1, border: '1px solid', borderColor: 'divider', width: '100%' }}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body2">
                {t('documentTemplate.previewEmpty', 'The preview renders here as you type')}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
